import React from "react";
import { TableCellsIcon } from "@heroicons/react/24/outline";
import './ColumnTable.css';
// Define the shape of each row in the data
export interface ColumnInfo {
  name: string;
  inferred_type: string;
  missing_pct_sample: number;
}

// Define the shape of each column configuration
interface ColumnConfig<T> {
  key: keyof T;
  label: string;
  width?: string;
  render?: (value: T[keyof T], row: T) => React.ReactNode;
}
interface ColumnTableProps {
  data: ColumnInfo[];
}
export default function ColumnTable({ data }: ColumnTableProps): JSX.Element {
  const columns: ColumnConfig<ColumnInfo>[] = [
    { key: "name", label: "Column Name", width: "40%" },
    { key: "inferred_type", label: "Inferred Type", width: "30%" },
    {
      key: "missing_pct_sample",
      label: "Missing % (Sample)",
      width: "30%",
      render: (value) => `${((value as number) * 100).toFixed(2)}%`,
    },
  ];

  return (
    <div className="card p-6 w-full">
      <h2 className="text-xl font-semibold text-secondary-900 mb-6 flex items-center">
        <TableCellsIcon className="w-6 h-6 text-primary-600 mr-3" />
        Column Information
      </h2>

      <div className="overflow-y-auto overflow-x-scroll max-h-[400px] scroll-container">
        <table className="w-full table-auto border-collapse">
          <thead>
            <tr className="bg-secondary-50">
              {columns.map((column, index) => (
                <th
                  key={index}
                  className="px-4 py-3 text-left text-sm font-medium text-secondary-700 border-b text-center"
                  style={{ width: column.width }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`${
                    rowIndex % 2 === 1 ? "bg-secondary-25" : ""
                  } hover:bg-secondary-50`}
                >
                  {columns.map((column, colIndex) => (
                    <td
                      key={colIndex}
                      className="px-4 py-3 text-sm text-secondary-900 border-b text-center"
                    >
                      {column.render
                        ? column.render(row[column.key], row)
                        : row[column.key]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-secondary-500"
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
